import React, { useLayoutEffect } from 'react';

import { AUTH_MESSAGES } from '../constants/auth.constants';

interface LogoutPopUpProps {
    isOpen: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

const LogoutPopUp: React.FC<LogoutPopUpProps> = ({ isOpen, onConfirm, onCancel }) => {
    useLayoutEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.height = '100%';
            document.body.style.touchAction = 'none';
            const preventWheel = (e: WheelEvent) => {
                e.preventDefault();
                e.stopPropagation();
                return false;
            };
            const preventTouch = (e: TouchEvent) => {
                e.preventDefault();
                e.stopPropagation();
                return false;
            };
            const preventKeys = (e: KeyboardEvent) => {
                if (e.key !== 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                }
            };
            document.addEventListener('wheel', preventWheel, { passive: false });
            document.addEventListener('touchmove', preventTouch, { passive: false });
            document.addEventListener('keydown', preventKeys);
            return () => {
                document.body.style.overflow = '';
                document.body.style.position = '';
                document.body.style.width = '';
                document.body.style.height = '';
                document.body.style.touchAction = '';
                document.removeEventListener('wheel', preventWheel);
                document.removeEventListener('touchmove', preventTouch);
                document.removeEventListener('keydown', preventKeys);
            };
        } else {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.height = '';
            document.body.style.touchAction = '';
        }
    }, [isOpen]);

    const handleBackdropClick = (e: React.MouseEvent | React.WheelEvent | React.TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
    };

    useLayoutEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onCancel();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onCancel]);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden bg-black bg-opacity-50 backdrop-blur-sm pointer-events-auto"
            onClick={handleBackdropClick}
            onWheel={handleBackdropClick}
            onTouchMove={handleBackdropClick}
        >
            <div className="relative w-full max-w-[558px] sm:max-w-[562px] mx-auto px-[20px] sm:px-0 pointer-events-none">
                <div
                    className="relative flex flex-col items-start self-stretch w-full p-[40px] rounded-md bg-white outline-none focus:outline-none pointer-events-auto shadow-[0_10px_30px_0_rgba(12,17,29,0.14)]"
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        onClick={onCancel}
                        type="button"
                        aria-label={AUTH_MESSAGES.LOGOUT_POPUP_CLOSE_ARIA}
                        className="absolute top-[24px] right-[24px] w-[20px] h-[20px] p-0 border-0 bg-transparent cursor-pointer flex items-center justify-center"
                    >
                        <svg width="20px" height="20px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="#667085" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                    <div className="flex flex-col gap-[6px] mb-[20px] self-stretch">
                        <h3 className="font-noto text-lg font-semibold leading-[1.5] m-0 text-[#030407]">
                            {AUTH_MESSAGES.LOGOUT_POPUP_TITLE}
                        </h3>
                        <p className="self-stretch text-[#667085] font-noto text-base not-italic font-normal leading-[1.5] m-0 pr-0">
                            {AUTH_MESSAGES.LOGOUT_POPUP_BODY}
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row items-center justify-start w-full sm:w-auto sm:mt-0">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="font-noto flex w-full sm:w-auto h-[52px] px-[20px] py-[14px] justify-center items-center gap-1.5 rounded-md bg-[#030407] text-white not-italic font-bold text-sm leading-5 cursor-pointer border-0 outline-none"
                        >
                            {AUTH_MESSAGES.LOGOUT_POPUP_CANCEL}
                        </button>
                        <button
                            type="button"
                            onClick={onConfirm}
                            className="font-noto flex w-full sm:w-auto h-[52px] px-[20px] py-[14px] justify-center items-center gap-1.5 bg-transparent text-[#667085] not-italic font-bold text-sm leading-5 border-0 cursor-pointer outline-none"
                        >
                            {AUTH_MESSAGES.LOGOUT_POPUP_CONFIRM}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LogoutPopUp;
